import { gql } from '@apollo/client';

/**
 * Spec 0011 (survey constructor) — ERP-scope mutations. Operation names differ
 * from the backend field names (`createSurvey`, `updateSurvey`, …) on purpose:
 * matching names collapse the generated result types (see graphql-codegen
 * skill). Every mutation returns the `OutputMixin` envelope — `success` +
 * `customErrors { code field message }` — which the `core/hooks/Surveys`
 * layer maps onto readable toasts/form errors (SURVEY_LOCKED,
 * DUPLICATE_ASSIGNMENT, COURSE_MISMATCH, HAS_RESPONSES, ARCHIVED,
 * LESSON_NOT_IN_GROUP, INVALID_QUESTION).
 */

export const CreateSurveyMutationDocument = gql`
  mutation SaveNewSurvey($input: SurveyInput!) {
    createSurvey(input: $input) {
      success
      customErrors {
        code
        field
        message
      }
      survey {
        id
      }
    }
  }
`;

export const UpdateSurveyMutationDocument = gql`
  mutation SaveExistingSurvey($id: ID!, $input: SurveyInput!) {
    updateSurvey(id: $id, input: $input) {
      success
      customErrors {
        code
        field
        message
      }
      survey {
        id
      }
    }
  }
`;

export const ArchiveSurveyMutationDocument = gql`
  mutation ToggleSurveyArchive($id: ID!, $isArchived: Boolean!) {
    archiveSurvey(id: $id, isArchived: $isArchived) {
      success
      customErrors {
        code
        field
        message
      }
      survey {
        id
        isArchived
      }
    }
  }
`;

export const DuplicateSurveyMutationDocument = gql`
  mutation CloneSurvey($id: ID!) {
    duplicateSurvey(id: $id) {
      success
      customErrors {
        code
        field
        message
      }
      survey {
        id
      }
    }
  }
`;

export const AssignSurveyToGroupMutationDocument = gql`
  mutation AttachSurveyToGroup(
    $surveyId: ID!
    $groupId: ID!
    $triggerLessonId: ID!
  ) {
    assignSurveyToGroup(
      surveyId: $surveyId
      groupId: $groupId
      triggerLessonId: $triggerLessonId
    ) {
      success
      customErrors {
        code
        field
        message
      }
      assignment {
        id
      }
    }
  }
`;

export const UpdateSurveyAssignmentMutationDocument = gql`
  mutation EditSurveyAssignment(
    $id: ID!
    $isActive: Boolean
    $triggerLessonId: ID
  ) {
    updateSurveyAssignment(
      id: $id
      isActive: $isActive
      triggerLessonId: $triggerLessonId
    ) {
      success
      customErrors {
        code
        field
        message
      }
      assignment {
        id
        isActive
      }
    }
  }
`;

export const DeleteSurveyAssignmentMutationDocument = gql`
  mutation RemoveSurveyAssignment($id: ID!) {
    deleteSurveyAssignment(id: $id) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/**
 * Spec 0027 — replace the survey's whole targeting block in ONE call.
 *
 * `success: true` WITH a populated `targeting.skipped[]` is the normal
 * outcome: the rules saved, and some groups simply have no lesson N. Only
 * rule-level problems (INVALID_PLAN / COURSE_MISMATCH / PLAN_LOCKED /
 * ARCHIVED / NOT_FOUND) come back as customErrors with success=false.
 */
export const SetSurveyTargetingMutationDocument = gql`
  mutation SaveSurveyTargeting($surveyId: ID!, $plans: [SurveyPlanInput!]!) {
    setSurveyTargeting(surveyId: $surveyId, plans: $plans) {
      success
      customErrors {
        code
        field
        message
      }
      targeting {
        assignmentsCreated
        assignmentsAdopted
        assignmentsDeactivated
        assignmentsDeleted
        skipped {
          groupId
          groupName
          reason
        }
        plans {
          id
          scope
          courseId
          courseName
          onlyActiveGroups
          triggerKind
          triggerLessonNumber
          skipPastLessons
          isActive
          assignmentsCount
          respondedCount
          hasResponses
          groups {
            groupId
            groupName
          }
        }
      }
    }
  }
`;

/**
 * Spec 0038 — bulk MANUAL attach. Deliberately NOT `setSurveyTargeting`:
 * that one replaces the whole targeting block, so «добавить группы» built on
 * it would be read-modify-write and one dropped field would destroy a rule.
 * `success: true` WITH a non-empty `skipped[]` is the normal outcome.
 */
export const AttachSurveyToGroups = gql`
  mutation AttachSurveyToGroups(
    $surveyId: ID!
    $groupIds: [ID!]!
    $triggerKind: String!
    $triggerLessonNumber: Int
  ) {
    assignSurveyToGroups(
      surveyId: $surveyId
      groupIds: $groupIds
      triggerKind: $triggerKind
      triggerLessonNumber: $triggerLessonNumber
    ) {
      success
      customErrors {
        code
        field
        message
      }
      createdCount
      skipped {
        groupId
        groupName
        reason
      }
    }
  }
`;

/** Bulk-assign question topics (spec 0038 revision). */
export const SaveQuestionMetrics = gql`
  mutation SaveQuestionMetrics($items: [QuestionMetricInput!]!) {
    setQuestionMetrics(items: $items) {
      success
      customErrors {
        code
        field
        message
      }
      changedCount
    }
  }
`;

/**
 * Spec 0098 — «Сгенерировать сводку». Refusals arrive as GraphQL errors with
 * `extensions.code` (digest_disabled, digest_running, digest_cooldown…).
 */
export const RequestSurveyDigest = gql`
  mutation RequestSurveyDigest(
    $scope: String!
    $scopeId: ID!
    $lessonNumber: Int
  ) {
    generateSurveyDigest(
      scope: $scope
      scopeId: $scopeId
      lessonNumber: $lessonNumber
    ) {
      id
      scope
      scopeId
      lessonNumber
      status
      textRu
      textPl
      model
      error
      createdAt
      finishedAt
      byName
    }
  }
`;
